import React, { useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"

const ConfirmMerge = ({
  selectedTickets,
  primaryTicketId,
  onConfirm,
  onBack,
  loading = false,
}) => {
  const [confirmNotes, setConfirmNotes] = useState({
    primary: `This ticket was merged with ${selectedTickets
      .filter((t) => t.ticketID !== primaryTicketId)
      .map((t) => `#${t.ticketID}`)
      .join(", ")}`,
    secondary: `This ticket was merged into #${primaryTicketId}`,
  })

  const handleConfirm = () => {
    onConfirm(confirmNotes)
  }

  return (
    <div className="flex flex-col h-full w-full max-w-none overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
      {/* Top header with buttons on the right */}
      <div className="absolute top-5 right-[90px] flex justify-end">
        <div className="flex gap-3">
          <Button 
            variant="admin_secondary" 
            onClick={onBack} 
            className="min-w-[120px]"
          >
            Back
          </Button>

          <Button
            variant="admin_primary"
            onClick={handleConfirm}
            loading={loading}
            className="min-w-[120px]"
          >
            Confirm
          </Button>
        </div>
      </div>

      <div className="flex flex-1 w-full mb-6 justify-between">
        {/* Left Side - Merge Summary */}
        <div className="w-1/2 flex flex-col gap-4 bg-admin_paper rounded-3xl border border-admin_bordered overflow-hidden p-6">
          <Typography variant="admin_h4">
            Merge Summary
          </Typography>

          <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
            {/* Primary ticket */}
            <div className="mb-4">
              <Typography 
                variant="admin_body_accent" 
                className="mt-0 mb-2"
              >
                Primary ticket:
              </Typography>

              <div className="bg-admin_canvas border border-admin_warning/50 rounded-md p-7 relative">
                {selectedTickets.find(
                  (t) => t.ticketID === primaryTicketId
                ) && (
                  <>
                    <div className="flex items-center justify-between">
                      <Typography
                        variant="admin_body"
                        className="text-[18px] xl:text-[20px] font-medium truncate mt-0 mb-1"
                      >
                        {
                          selectedTickets.find(
                            (t) => t.ticketID === primaryTicketId
                          ).summary
                        }
                      </Typography>

                      <div className="absolute top-2 right-2 bg-admin_warning text-sm text-admin_text_inv rounded-sm px-2 py-0.5">
                        Primary
                      </div>
                    </div>

                    <Typography variant="admin_txt1" className="text-admin_text">
                      #{primaryTicketId}
                    </Typography>

                    <Typography 
                      variant="admin_body_dim" 
                      className="italic mt-2 mb-0"
                    >
                      This ticket will remain open and contain all conversations
                    </Typography>
                  </>
                )}
              </div>
            </div>

            {/* Secondary tickets */}
            <div>
              <Typography 
                variant="admin_body" 
                className="font-bold mb-2"
              >
                Secondary tickets (
                {
                  selectedTickets.filter((t) => t.ticketID !== primaryTicketId)
                    .length
                }
                ):
              </Typography>

              <div className="max-h-[calc(100vh-400px)] bg-admin_neutral border border-admin_bordered  rounded-md overflow-y-auto">
                {selectedTickets
                  .filter((t) => t.ticketID !== primaryTicketId)
                  .map((ticket) => (
                    <div
                      key={ticket.ticketID}
                      className="border-b border-admin_bordered last:border-0 p-7"
                    >
                      <Typography
                        variant="admin_body"
                        className="text-[18px] xl:text-[20px] font-medium truncate my-0"
                      >
                        {ticket.summary}
                      </Typography>

                      <Typography 
                        variant="admin_body"
                        className="mt-2 mb-0">
                        #{ticket.ticketID}
                        {ticket.from && (
                          <span className="truncate ml-2">
                            From: {ticket.from}
                          </span>
                        )}
                      </Typography>


                      <Typography
                        variant="admin_body_dim"
                        className="italic mb-0"
                      >
                        This ticket will be closed and link to the primary
                        ticket
                      </Typography>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Notes */}
        <div className="w-[calc(50%-1px)] flex flex-col p-6 pr-0 overflow-hidden">
          <Typography variant="admin_h4" className="mb-4">
            Merge Notes
          </Typography>

          <Typography
            variant="admin_body_dim"
            className="mt-2 mb-6"
          >
            These notes will be added to each ticket to document this merge
            operation.
          </Typography>

          {/* Primary ticket note */}
          <div className="mb-4">
            <Typography 
              variant="admin_body" 
              className="font-bold mb-2">
              Note to add to primary ticket:
            </Typography>

            <textarea
              className="w-full h-32 text-admin_text bg-admin_paper border border-admin_bordered rounded-md focus:outline-none focus:ring-1 focus:ring-admin_primary p-3"
              value={confirmNotes.primary}
              onChange={(e) =>
                setConfirmNotes({ ...confirmNotes, primary: e.target.value })
              }
            />
          </div>

          {/* Secondary tickets note */}
          <div>
            <Typography variant="admin_body" className="font-bold mb-2">
              Note to add to secondary tickets:
            </Typography>
            <textarea
              className="w-full h-32 text-admin_text bg-admin_paper border border-admin_bordered rounded-md focus:outline-none focus:ring-1 focus:ring-admin_primary p-3"
              value={confirmNotes.secondary}
              onChange={(e) =>
                setConfirmNotes({
                  ...confirmNotes,
                  secondary: e.target.value,
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Footer with action buttons - added padding for space */}
      <div className="flex justify-end gap-3 pb-8 mb-4 mr-[70px]">
        <Button 
          variant="admin_secondary" 
          onClick={onBack} 
          className="min-w-[120px]"
        >
          Back
        </Button>

        <Button
          variant="admin_primary"
          onClick={handleConfirm}
          loading={loading}
          className="min-w-[120px]"
        >
          Confirm
        </Button>
      </div>
    </div>
  )
}

export default ConfirmMerge
